/**
 * LocalRuntime — IAgentRuntime implementation that runs the agent in the same process.
 *
 * Mirror image of AgentClient: same interface, no WebSocket.
 * Uses InProcessTransport + startAgentServer() internally.
 *
 * Usage:
 *   const runtime = new LocalRuntime();
 *   runtime.onEvent(e => console.log(e));
 *   await runtime.start({ projectDir: "/path/to/workspace" });
 *   await runtime.prompt("Hello");
 *   await runtime.dispose();
 *
 * @docLink packages/sdk/concepts#local-runtime
 */

import { startAgentServer } from "@skaile/workspaces/runner";
import type { AgentEvent, IAgentRuntime } from "@skaile/workspaces/types";
import { InProcessTransport } from "./transport.js";

/**
 * Options passed to {@link LocalRuntime.start}.
 *
 * `projectDir` is the only required field — it points to the workspace directory
 * containing `skaile.yaml`. All other fields override the manifest's agent configuration.
 *
 * @docLink packages/sdk/concepts#local-runtime
 */
export interface LocalRuntimeStartOptions {
  projectDir: string;
  agentDir?: string;
  driver?: string;
  model?: string;
  provider?: string;
  onLog?: (line: string) => void;
}

export class LocalRuntime implements Omit<IAgentRuntime, "start" | "configure"> {
  private readonly transport = new InProcessTransport();
  private readonly handlers = new Set<(e: AgentEvent) => void>();
  private started = false;
  private resolveReady!: () => void;
  private readonly readyPromise = new Promise<void>((resolve) => {
    this.resolveReady = resolve;
  });

  constructor() {
    this.transport.onEvent((e) => {
      for (const h of this.handlers) h(e);
    });
  }

  /**
   * Start the agent server. Resolves when the runner is wired and ready
   * to accept commands. Must be called before prompt() / reply().
   */
  async start(opts: LocalRuntimeStartOptions): Promise<void> {
    if (this.started) return;
    this.started = true;
    await startAgentServer({
      projectDir: opts.projectDir,
      agentDir: opts.agentDir,
      driver: opts.driver,
      model: opts.model,
      provider: opts.provider,
      transport: this.transport,
      onLog: opts.onLog,
    });
    this.resolveReady();
  }

  /**
   * Resolves once the runner is wired and accepting commands. Await this
   * before issuing commands that expect a reply (e.g. debug queries) so a
   * pre-start command isn't buffered indefinitely. Never rejects.
   */
  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  async prompt(message: string): Promise<void> {
    this.transport.inject({ type: "prompt", prompt: message });
  }

  async reply(answer: string): Promise<void> {
    this.transport.inject({ type: "reply", answer });
  }

  async abort(): Promise<void> {
    this.transport.inject({ type: "cancel" });
  }

  async dispose(): Promise<void> {
    this.transport.inject({ type: "shutdown" });
  }

  onEvent(handler: (e: AgentEvent) => void): void {
    this.handlers.add(handler);
  }

  offEvent(handler: (e: AgentEvent) => void): void {
    this.handlers.delete(handler);
  }

  get isRunning(): boolean {
    return this.transport.connected;
  }
}
